import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HitSkeletonCard } from "@/components/loading";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePublicConfig } from "@/lib/publicConfig";
import { makeCardImgFallback, useProactiveResolve } from "@/lib/imageFallback";
import { cardPath } from "@/lib/cardUrl";
import { Seo } from "@/components/Seo";

// ============================================================================
// HIT TCG — Home v11 (Feb 2026)
// Estructura visual basada en `hittcg-landing-standalone.html` (spec Emergent).
// Mobile-first: todos los tamaños con clamp() y grid que colapsa a 1 col en <768px.
// Fonts + hit-fx.js se cargan solo en esta ruta (no globales).
// ============================================================================

const money = (n) => (n == null ? "—" : "$" + Number(n).toFixed(2));
const sellers = (n) => (!n ? "1 vendedor" : n + (n === 1 ? " vendedor" : " vendedores"));
const GAME_LABEL = { pokemon: "Pokémon", riftbound: "Riftbound", yugioh: "Yu-Gi-Oh!" };

function useHomeAssets(launchAt) {
  useEffect(() => {
    const links = [
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;700;800;900&family=JetBrains+Mono:wght@700&display=swap" },
    ];
    const added = links.map((l) => {
      const el = document.createElement("link");
      Object.entries(l).forEach(([k, v]) => v && el.setAttribute(k === "crossOrigin" ? "crossorigin" : k, v));
      document.head.appendChild(el);
      return el;
    });
    return () => {
      added.forEach((el) => el.remove());
      if (window.__hitTimer) clearInterval(window.__hitTimer);
      if (window.__hitScroll) {
        window.removeEventListener("scroll", window.__hitScroll);
        window.removeEventListener("resize", window.__hitScroll);
      }
    };
  }, []);

  // hit-fx.js se inyecta DESPUÉS de conocer la fecha de lanzamiento anclada
  // en backend (config.launch_at). Sin este anclaje el contador se reseteaba
  // a "hoy - 21 días" en cada recarga/deploy — ahora es histórico permanente.
  useEffect(() => {
    if (!launchAt) return;
    window.__hitLaunchDate = launchAt;
    const s = document.createElement("script");
    s.src = "/hit-fx.js?v=" + encodeURIComponent(launchAt);
    s.async = true;
    document.body.appendChild(s);
    return () => { s.remove(); };
  }, [launchAt]);
}

export default function Home() {
  const { user } = useAuth();
  const [showcase, setShowcase] = useState(null);
  const [stores, setStores] = useState([]);
  const publicConfig = usePublicConfig();
  const social = publicConfig.social_links || { instagram: "", tiktok: "", whatsapp: "" };
  useHomeAssets(publicConfig.launch_at);

  useEffect(() => {
    api.get("/home/showcase").then((r) => setShowcase(r.data)).catch(() => {});
    api.get("/partner-stores").then((r) => setStores(r.data?.stores || [])).catch(() => {});
  }, []);

  const featured = showcase?.featured || [];
  const priceCard = showcase?.price_showcase;
  const totalActive = showcase?.total_active_publications || 0;
  // Fans decorativos (hero + vendedores): renderizamos SOLO los slots que
  // tienen imagen real. Sin datos → no montamos el fan (evita placeholders
  // rayados que se veían feos cuando había 1-2 cartas y 3 slots).
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const fanImgs = featured.map((f) => f.image_url).filter(Boolean);
  const heroFan = fanImgs.slice(0, 3);
  const venderFan = fanImgs.slice(0, 2);
  // Tiendas aliadas: siempre completamos hasta 6 celdas — si hay menos, rellenamos
  // con placeholders numerados para preservar la grilla del diseño original.
  const storeCells = [...stores];
  while (storeCells.length < 6) storeCells.push({ __placeholder: true, id: `ph-${storeCells.length}`, name: `LOGO TIENDA 0${storeCells.length + 1}` });
  // Loop visual del carrusel: renderizamos DOS COPIAS del array completo
  // (una visible + una "gemela" para el wrap del CSS). Nunca repetimos cards
  // individualmente — evita ver "dos Slowbro iguales" en la misma vista.
  // El backend ya garantiza unicidad y variedad (random sample server-side).
  const railItems = featured;
  const railLoop = featured.length ? [...featured, ...featured] : [];

  // v23 — Resolución PROACTIVA de imagen: los items del rail traen
  // `image_url` de TCGdex; si el CDN cae, resolvemos vía backend usando
  // set_id + local_id / number. Home_showcase debe incluir estos campos
  // en la respuesta (si no vienen, el hook simplemente no encuentra
  // candidatos y no dispara requests — es idempotente).
  const resolvedFeatured = useProactiveResolve(featured, (f) => ({
    id: f.card_id,
    image_url: f.image_url,
    set_id: f.set_id,
    number: f.local_id || f.number,
  }));

  return (
    <div className="hit-home" style={{ fontFamily: "'Archivo', system-ui, sans-serif", background: "#080808", color: "#fff" }}>
      <Seo
        title="HIT TCG — El primer mercado de cartas hecho en Ecuador"
        description="Compra, vende y descubre el precio real de tus cartas Pokémon en Ecuador. Pagos protegidos, envíos nacionales, precios en USD hechos por la comunidad."
        url="/"
      />
      <HomeStyles />

      {/* HERO */}
      <section className="hh-hero">
        <div data-px="slab" className="hh-slab" />
        <div className="hh-halo" />
        <div className="hh-grid-bg" />
        <span data-px="ec" className="hh-ec">EC</span>
        <div data-px="fan" className="hh-fan">
          {heroFan[0] && <div className="hh-card hh-card-a"><img src={heroFan[0]} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.parentElement.remove()} /></div>}
          {heroFan[1] && <div className="hh-card hh-card-b"><img src={heroFan[1]} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.parentElement.remove()} /></div>}
          {heroFan[2] && <div className="hh-card hh-card-c"><img src={heroFan[2]} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.parentElement.remove()} /></div>}
        </div>
        <div className="hh-container hh-hero-inner">
          <div className="hh-hero-line" />
          <span className="hh-mono hh-badge">HECHO EN ECUADOR · 2026</span>
          <h1 className="hh-h1">
            El primer mercado<br />
            de cartas <em>hecho en</em><br />
            Ecuador.
          </h1>
          <p className="hh-lede">
            Compra, vende y descubre el precio real de tus cartas Pokémon — en español, con envíos dentro del país y pagos protegidos.
          </p>
          <form className="hh-search" onSubmit={(e) => { e.preventDefault(); window.location.href = "/catalog?q=" + encodeURIComponent(e.target.q.value || ""); }}>
            <input name="q" placeholder='Escribe pedazos, como "char" o "pika"…' aria-label="Buscar cartas" />
            <button type="submit" data-testid="hh-hero-search">Buscar</button>
          </form>
          <div className="hh-hero-metrics">
            <div><div className="hh-metric-n">100%</div><div className="hh-mono">PAGO PROTEGIDO</div></div>
            <div><div className="hh-metric-n">100%</div><div className="hh-mono">VERIFICADOS</div></div>
            <div><div className="hh-metric-n">USD</div><div className="hh-mono">PRECIOS LOCALES</div></div>
            <div className="hh-counter">
              <div className="hh-counter-l">
                <span className="hh-pulse" /><div>
                  <div className="hh-mono" style={{ color: "#fff" }}>EL PRIMERO DE ECUADOR</div>
                  <div className="hh-mono" style={{ opacity: .5 }}>EN LÍNEA DESDE EL DÍA UNO</div>
                </div>
              </div>
              <div className="hh-counter-r">
                <Digit v="days" label="DÍAS" />
                <Sep />
                <Digit v="hours" label="HORAS" />
                <Sep />
                <Digit v="min" label="MIN" />
                <Sep />
                <Digit v="sec" label="SEG" coral />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="hh-ticker" aria-hidden>
        <div className="hh-ticker-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i}>
              Pago protegido <b>◆</b> Español · Inglés · Japonés <b>◆</b> Envíos a todo el país <b>◆</b> Precio real ecuatoriano <b>◆</b> Entrega en persona <b>◆</b>&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* 01 — PRECIO REAL */}
      <section className="hh-precio">
        <div className="hh-precio-band" />
        <div className="hh-container hh-precio-grid">
          <span className="hh-vlabel hh-mono">01 — PRECIO REAL</span>
          <div className="hh-precio-copy">
            <h2 className="hh-h2">El precio de las cartas en Ecuador, hecho por la comunidad.</h2>
            <p className="hh-lede">Ya no tienes que adivinar cuánto vale tu carta ni copiar precios de páginas de afuera. Aquí el mercado ecuatoriano define el valor real, con la oferta y demanda local — se actualiza con cada publicación.</p>
            <div className="hh-mono hh-tags"><span>Cada idioma con su propio índice</span><span>Histórico de ventas del país</span></div>
          </div>
          <div className="hh-precio-cards">
            <div className="hh-pcard hh-pcard-green">
              <div className="hh-mono">PRECIO MÁS BAJO</div>
              <div className="hh-pcard-n"><span style={{ color: "#34D399" }}>{money(priceCard?.lowest_price)}</span></div>
              <div className="hh-pcard-h">para comprar ya</div>
              <p>El más barato disponible entre los vendedores activos{priceCard ? ` para ${priceCard.name}` : ""}.</p>
            </div>
            <div className="hh-pcard hh-pcard-coral">
              <div className="hh-mono">PRECIO DE MERCADO EC</div>
              <div className="hh-pcard-n"><span style={{ color: "#FF4438" }}>{money(priceCard?.market_price)}</span></div>
              <div className="hh-pcard-h">referencia real</div>
              <p>Promedio de la oferta activa en el país, actualizado en cada publicación.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 02 — CARTAS DEL MOMENTO */}
      <section className="hh-momento">
        <div className="hh-container">
          <div className="hh-momento-head">
            <div>
              <span className="hh-mono" style={{ color: "#FF4438" }}>02 — DESTACADOS</span>
              <h2 className="hh-h2">Cartas del momento</h2>
            </div>
            <div className="hh-momento-cta">
              <span className="hh-mono">+{totalActive.toLocaleString()} PUBLICADAS</span>
              <Link to="/catalog" className="hh-link" data-testid="hh-goto-catalog">Ver catálogo →</Link>
            </div>
          </div>
        </div>
        <div className="hh-rail-mask">
          <div data-px="rail" className={"hh-rail" + (railItems.length ? "" : " hh-rail-empty")}>
            {railLoop.length > 0 ? railLoop.map((c, i) => (
              <Link
                key={`${i}-${c.card_id}-${c.language}`}
                to={cardPath({ cardId: c.card_id, name: c.name, language: (c.language || "es").toLowerCase() })}
                className="hh-rail-card"
                style={{ transform: i % 2 === 0 ? "translateY(0)" : "translateY(38px)" }}
                data-testid={i < railItems.length ? `hh-rail-card-${c.card_id}` : undefined}
                aria-hidden={i >= railItems.length ? "true" : undefined}
              >
                <div className="hh-rail-img">
                  <img
                    src={c.image_url || resolvedFeatured[c.card_id] || ""}
                    alt={c.name}
                    loading="lazy"
                    decoding="async"
                    onError={makeCardImgFallback((img) => { img.style.display = "none"; })}
                  />
                  <span className="hh-lang-badge">{c.language || "—"}</span>
                </div>
                <div className="hh-rail-name">{c.name}</div>
                <div className="hh-mono hh-rail-meta" data-testid={i < railItems.length ? `hh-rail-game-${c.card_id}` : undefined}>{GAME_LABEL[c.game] || "Pokémon"} · {c.set_name || "—"} · {sellers(c.sellers_count)}</div>
                <div className="hh-rail-price">{money(c.price)}{c.graded_only && <span className="hh-mono" style={{ fontSize: 11, marginLeft: 6, color: "#34D399" }}>GRADUADA</span>}</div>
              </Link>
            )) : Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="hh-rail-card hh-rail-skel" style={{ transform: i % 2 === 0 ? "translateY(0)" : "translateY(38px)" }}><HitSkeletonCard /></div>
            ))}
          </div>
        </div>
        <div className="hh-container hh-momento-foot">
          <span className="hh-tick" /><span className="hh-mono">ROTANDO EN VIVO · PASA EL CURSOR PARA PAUSAR</span>
        </div>
      </section>

      {/* 03 — POR QUÉ HITTCG */}
      <section className="hh-porque">
        <div className="hh-container hh-porque-grid">
          <div>
            <span className="hh-mono" style={{ color: "#FF4438" }}>03 — POR QUÉ HITTCG</span>
            <h2 className="hh-h2">Cartas nuestras,<br /><span style={{ color: "#FF4438" }}>precios nuestros.</span></h2>
            <p className="hh-lede">Cuatro razones por las que la escena ecuatoriana está migrando aquí.</p>
          </div>
          <ol className="hh-porque-list">
            {[
              ["01", "Precios reales, hechos aquí", "Construidos con lo que de verdad se vende en Ecuador, no traducidos de afuera."],
              ["02", "En tu idioma", "Aceptamos cartas en español, inglés y japonés, cada una con su propio precio."],
              ["03", "Compra sin miedo", "Tu pago queda protegido hasta que confirmes que recibiste la carta."],
              ["04", "Envíos dentro de Ecuador", "Recíbelas en cualquier provincia, o encuéntrate en persona en tu ciudad."],
            ].map(([n, t, d]) => (
              <li key={n}><span className="hh-mono">{n}</span><h3>{t}</h3><p>{d}</p></li>
            ))}
          </ol>
        </div>
      </section>

      {/* 04 — TIENDAS ALIADAS */}
      <section className="hh-tiendas">
        <span data-px="respaldo" className="hh-ghost">RESPALDO</span>
        <div className="hh-container hh-tiendas-grid">
          <div>
            <span className="hh-mono" style={{ color: "#FF4438" }}>04 — TIENDAS ALIADAS</span>
            <h2 className="hh-h2">Tiendas que nos respaldan</h2>
            <p className="hh-lede">Tiendas físicas y vendedores con años en la escena ya publican su catálogo en HitTCG. Si tienes tienda, escríbenos y apareces aquí.</p>
            <a href={social.whatsapp || "#"} className="hh-btn-white" target="_blank" rel="noopener">Quiero ser tienda aliada →</a>
          </div>
          <div className="hh-tiendas-logos">
            {storeCells.map((s) => {
              if (s.__placeholder) {
                return <div key={s.id} className="hh-tienda-cell hh-tienda-placeholder"><span className="hh-mono">{s.name}</span></div>;
              }
              const inner = (
                <>
                  {s.logo_url ? (
                    <img src={backendUrl + s.logo_url} alt={s.name} loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <span className="hh-mono">{s.name}</span>
                  )}
                </>
              );
              return s.has_catalog && s.seller_id ? (
                <Link key={s.id} to={`/vendedor/${s.seller_id}`} className="hh-tienda-cell" data-testid="hh-store-cell">{inner}</Link>
              ) : (
                <div key={s.id} className="hh-tienda-cell" data-testid="hh-store-cell">{inner}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 05 — NOVEDADES → OCULTA (Feb 2026 v11, reactivar cuando esté el backend) */}
      {/* <section className="hh-novedades">…</section> */}

      {/* 06 — FAQ */}
      <section className="hh-faq">
        <span className="hh-ghost">FAQ</span>
        <div className="hh-container hh-faq-grid">
          <div>
            <span className="hh-mono" style={{ color: "#FF4438" }}>06 — PREGUNTAS FRECUENTES</span>
            <h2 className="hh-h2">Lo que todos preguntan</h2>
            <p className="hh-lede">Si te queda una duda que no está aquí, escríbenos por WhatsApp — respondemos el mismo día.</p>
            {social.whatsapp && <a href={social.whatsapp} className="hh-btn-white" target="_blank" rel="noopener">Preguntar por WhatsApp →</a>}
            <div style={{ marginTop: 16 }}><Link to="/faq" className="hh-link">Ver todas las preguntas →</Link></div>
          </div>
          <ol className="hh-faq-list">
            {[
              ["01", "¿Cómo sé que el vendedor es confiable?", "Todo vendedor pasa por verificación de identidad antes de publicar, y su historial de ventas queda visible en su perfil."],
              ["02", "¿Qué pasa con mi dinero mientras llega la carta?", "Tu pago queda retenido por HitTCG y solo se libera al vendedor cuando confirmas que recibiste la carta en el estado acordado."],
              ["03", "¿Cuánto cuesta publicar mis cartas?", "Publicar es gratis y sin límite de cartas. Solo se cobra una comisión cuando la venta se concreta."],
              ["04", "¿Cómo se calcula el precio de mercado?", "Con la oferta activa y las ventas reales dentro de Ecuador. Cada idioma (ES, EN, JP) tiene su propio índice, sin mezclar valores."],
              ["05", "¿Hacen envíos a todo el país?", "Sí, a cualquier provincia. También puedes acordar entrega en persona dentro de tu ciudad."],
            ].map(([n, q, a]) => (
              <li key={n}><span className="hh-mono">{n}</span><div><h3>{q}</h3><p>{a}</p></div></li>
            ))}
          </ol>
        </div>
      </section>

      {/* 07 — COMUNIDAD */}
      <section className="hh-comunidad">
        <div className="hh-comunidad-tex" aria-hidden />
        <div className="hh-container hh-comunidad-inner">
          <div className="hh-comunidad-head">
            <div>
              <span className="hh-mono" style={{ color: "rgba(255,255,255,.85)" }}>07 — COMUNIDAD</span>
              <h2 className="hh-h2" style={{ color: "#fff" }}>Únete a la comunidad<br />TCG de Ecuador</h2>
            </div>
            <p className="hh-lede" style={{ color: "rgba(255,255,255,.9)" }}>Drops, torneos, precios que se mueven y trades entre colecciones. Todo pasa primero en los canales.</p>
          </div>
          <div className="hh-comunidad-cards">
            <SocialCard href={social.whatsapp} tag="GRUPO ABIERTO" name="WhatsApp" desc="Avisos de nuevas cartas, dudas resueltas y soporte directo del equipo." cta="Entrar al grupo →" accent="#34D399" testid="hh-social-wa" />
            <SocialCard href={social.instagram} tag="@HITTCG.EC" name="Instagram" desc="Cartas del momento, sorteos y el detrás del marketplace." cta="Seguirnos →" accent="#FF4438" raised testid="hh-social-ig" />
            <SocialCard href={social.tiktok} tag="@HITTCG" name="TikTok" desc="Aperturas, pulls y precios explicados en menos de un minuto." cta="Ver videos →" accent="#fff" testid="hh-social-tk" />
          </div>
        </div>
      </section>

      {/* 08 — VENDEDORES */}
      <section className="hh-vender">
        <div className="hh-vender-slab" />
        <div className="hh-container hh-vender-grid">
          <div>
            <span className="hh-mono" style={{ color: "#FF4438" }}>08 — VENDEDORES</span>
            <h2 className="hh-h2">Sé parte del primer mercado nacional de cartas.</h2>
            <p className="hh-lede">Sube tu catálogo y dale un precio real a tu colección. Aquí tu carta vale lo que dice el mercado ecuatoriano — no una web de afuera.</p>
            <div className="hh-vender-ctas">
              <Link to={user ? "/publish" : "/register"} className="hh-btn-coral" data-testid="hh-vender-cta">Publicar mis cartas</Link>
              {social.whatsapp && <a href={social.whatsapp} className="hh-btn-ghost" target="_blank" rel="noopener">Hablar por WhatsApp</a>}
            </div>
            <div className="hh-mono" style={{ marginTop: 18, opacity: .5 }}>SIN COSTO POR PUBLICAR</div>
          </div>
          <div data-px="vender" className="hh-vender-fan">
            {venderFan[0] && <div className="hh-vender-card hh-vender-a"><img src={venderFan[0]} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.parentElement.remove()} /></div>}
            {venderFan[1] && <div className="hh-vender-card hh-vender-b"><img src={venderFan[1]} alt="" loading="lazy" decoding="async" onError={(e) => e.currentTarget.parentElement.remove()} /></div>}
          </div>
        </div>
      </section>

    </div>
  );
}

function Digit({ v, label, coral }) {
  return (
    <div className="hh-digit">
      <span data-up={v} style={coral ? { color: "#FF4438" } : undefined}>00</span>
      <div className="hh-mono">{label}</div>
    </div>
  );
}
function Sep() { return <span className="hh-sep">:</span>; }

function SocialCard({ href, tag, name, desc, cta, accent, raised, testid }) {
  const inner = (
    <>
      <span className="hh-mono" style={{ color: accent }}>{tag}</span>
      <div className="hh-social-name" style={{ borderColor: accent }}>{name}</div>
      <p>{desc}</p>
      <span className="hh-social-cta">{cta}</span>
    </>
  );
  const className = "hh-social-card" + (raised ? " hh-social-card-raised" : "");
  return href ? (
    <a href={href} target="_blank" rel="noopener" className={className} data-testid={testid}>{inner}</a>
  ) : (
    <div className={className + " hh-social-card-disabled"} data-testid={testid}>{inner}<span className="hh-mono hh-social-soon">PRÓXIMAMENTE</span></div>
  );
}

// ============================================================================
// Estilos scoped al home (evita colisiones con Tailwind global)
// ============================================================================
function HomeStyles() {
  return (
    <style>{`
      .hit-home { --coral:#FF4438; --coral-h:#ff6257; --green:#34D399; --line:rgba(255,255,255,.09); --line2:rgba(255,255,255,.14); --bg:#080808; --bg2:#0d0d0d; --bg3:#0e0e0e; --card:#111; --mono:'JetBrains Mono',ui-monospace,monospace; }
      .hit-home * { box-sizing:border-box; }
      .hit-home a { text-decoration:none; }
      .hit-home a:not([class]) { color:inherit; }
      .hh-container { max-width:1720px; margin:0 auto; padding:0 clamp(16px, 4vw, 44px); }
      .hh-mono { font-family:var(--mono); font-weight:700; text-transform:uppercase; letter-spacing:.14em; font-size:clamp(10px, 1.1vw, 11px); color:rgba(255,255,255,.55); }
      .hh-h1 { font-weight:800; font-size:clamp(44px, 8vw, 104px); line-height:.96; letter-spacing:-.045em; text-wrap:balance; margin:12px 0 16px; }
      .hh-h1 em { font-weight:400; font-style:italic; color:rgba(255,255,255,.55); }
      .hh-h2 { font-weight:800; font-size:clamp(30px, 5.4vw, 76px); line-height:.98; letter-spacing:-.04em; margin:8px 0 14px; text-wrap:balance; }
      .hh-lede { color:rgba(255,255,255,.62); font-size:clamp(14px, 1.4vw, 16px); line-height:1.55; max-width:52ch; text-wrap:pretty; }
      .hh-link { color:var(--coral); font-weight:700; }
      .hh-link:hover { color:var(--coral-h); }

      /* HERO */
      .hh-hero { position:relative; min-height:clamp(720px, 100vh, 940px); padding:clamp(70px, 12vw, 110px) 0 60px; overflow:hidden; }
      .hh-slab { position:absolute; left:-6%; top:-8%; width:118%; height:70%; background:linear-gradient(100deg, rgba(255,68,56,.16), transparent 62%); transform:rotate(-7deg); will-change:transform; pointer-events:none; }
      .hh-halo { position:absolute; right:-10%; top:10%; width:620px; height:620px; border-radius:50%; background:radial-gradient(circle, rgba(255,68,56,.18), transparent 65%); animation:hit-drift 16s ease-in-out infinite; pointer-events:none; }
      .hh-grid-bg { position:absolute; inset:0; background:linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px); background-size:120px 100%; pointer-events:none; opacity:.6; }
      .hh-ec { position:absolute; right:4%; top:20%; font-weight:900; font-size:clamp(160px, 24vw, 340px); color:transparent; -webkit-text-stroke:1px rgba(255,255,255,.07); pointer-events:none; will-change:transform; z-index:0; }
      .hh-fan { position:absolute; right:4%; bottom:0; display:none; will-change:transform; pointer-events:none; }
      @media (min-width:1100px) { .hh-fan { display:block; } }
      .hh-card { position:absolute; bottom:20px; border-radius:14px; background:repeating-linear-gradient(135deg, #1a1a1a 0 12px, #111 12px 24px); border:1px solid rgba(255,255,255,.08); box-shadow:0 40px 90px rgba(0,0,0,.7); overflow:hidden; }
      .hh-card img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
      .hh-card-a { width:300px; height:420px; right:200px; transform:rotate(-14deg); animation:hit-float-a 7s ease-in-out infinite; }
      .hh-card-b { width:330px; height:462px; right:80px; transform:rotate(-3deg); animation:hit-float-b 9s ease-in-out infinite; }
      .hh-card-c { width:372px; height:520px; right:-40px; transform:rotate(9deg); border-color:var(--coral); box-shadow:0 60px 120px rgba(0,0,0,.9), 0 0 90px rgba(255,68,56,.26); animation:hit-float-c 6s ease-in-out infinite; }
      .hh-hero-inner { position:relative; z-index:2; }
      .hh-hero-line { width:120px; height:2px; background:var(--coral); margin-bottom:20px; }
      .hh-badge { color:var(--coral); }
      .hh-search { display:flex; align-items:center; gap:8px; margin:22px 0 40px; max-width:640px; height:62px; background:rgba(255,255,255,.05); border:1px solid var(--line2); border-radius:999px; padding:6px 6px 6px 22px; backdrop-filter:blur(6px); }
      .hh-search input { flex:1; background:none; border:0; outline:0; color:#fff; font-size:clamp(14px, 1.6vw, 16px); font-family:inherit; }
      .hh-search input::placeholder { color:rgba(255,255,255,.45); }
      .hh-search button { height:50px; padding:0 clamp(20px, 3vw, 32px); border-radius:999px; background:#fff; color:#080808; font-weight:800; border:0; cursor:pointer; transition:background .2s; font-family:inherit; }
      .hh-search button:hover { background:var(--coral); color:#fff; }
      .hh-hero-metrics { display:grid; grid-template-columns:repeat(3, 1fr); gap:clamp(16px, 3vw, 32px); margin-top:32px; }
      @media (min-width:1100px) { .hh-hero-metrics { grid-template-columns:repeat(6, 1fr); } .hh-counter { grid-column:span 3; } }
      @media (min-width:1400px) { .hh-hero-metrics { grid-template-columns:repeat(12, 1fr); } .hh-counter { grid-column:7 / -1; } }
      .hh-metric-n { font-weight:800; font-size:clamp(26px, 3.4vw, 46px); line-height:1; margin-bottom:6px; }
      .hh-counter { grid-column:1 / -1; background:rgba(255,255,255,.035); border:1px solid var(--line); border-radius:18px; padding:16px; backdrop-filter:blur(6px); display:flex; flex-wrap:wrap; gap:16px; align-items:center; justify-content:space-between; }
      .hh-counter-l { display:flex; align-items:center; gap:12px; }
      .hh-pulse { width:10px; height:10px; border-radius:50%; background:var(--green); animation:hit-pulse 2s ease-in-out infinite; flex:none; }
      .hh-counter-r { display:flex; align-items:center; gap:6px; font-variant-numeric:tabular-nums; }
      .hh-digit { text-align:center; }
      .hh-digit span { display:block; font-weight:800; font-size:clamp(22px, 2.6vw, 30px); }
      .hh-sep { color:var(--coral); font-weight:800; margin:0 4px; }

      /* TICKER */
      .hh-ticker { background:var(--coral); color:#080808; overflow:hidden; padding:15px 0; }
      .hh-ticker-track { display:flex; width:max-content; animation:hit-marquee 26s linear infinite; font-weight:800; font-size:clamp(15px, 1.6vw, 20px); }
      .hh-ticker-track b { margin:0 14px; }

      /* 01 — PRECIO REAL */
      .hh-precio { position:relative; padding:clamp(60px, 8vw, 120px) 0; }
      .hh-precio-band { position:absolute; left:0; top:0; bottom:0; width:46%; background:var(--bg3); border-right:1px solid var(--line); display:none; }
      @media (min-width:900px) { .hh-precio-band { display:block; } }
      .hh-precio-grid { position:relative; display:grid; grid-template-columns:1fr; gap:32px; }
      @media (min-width:900px) { .hh-precio-grid { grid-template-columns:32px 1fr 1fr; gap:48px; align-items:start; } }
      .hh-vlabel { color:var(--coral); }
      @media (min-width:900px) { .hh-vlabel { writing-mode:vertical-rl; transform:rotate(180deg); align-self:start; } }
      .hh-tags { display:flex; flex-wrap:wrap; gap:16px; margin-top:16px; color:rgba(255,255,255,.55); }
      .hh-precio-cards { display:grid; gap:20px; }
      @media (min-width:900px) { .hh-precio-cards { position:relative; } .hh-pcard-green { transform:translateY(-20px); } .hh-pcard-coral { margin-left:56px; } }
      .hh-pcard { background:var(--card); border:1px solid var(--line2); border-radius:22px; padding:clamp(22px, 3vw, 34px); }
      .hh-pcard-n { font-weight:800; font-size:clamp(44px, 6vw, 62px); line-height:1.05; margin:14px 0 6px; display:block; }
      .hh-pcard-n span { display:inline-block; font-weight:inherit; font-size:inherit; line-height:inherit; }
      .hh-pcard-h { color:rgba(255,255,255,.5); font-size:14px; margin-bottom:14px; }
      .hh-pcard p { color:rgba(255,255,255,.6); font-size:14px; line-height:1.55; }

      /* 02 — CARTAS DEL MOMENTO */
      .hh-momento { padding:clamp(60px, 8vw, 120px) 0; background:var(--bg2); border-block:1px solid var(--line); }
      .hh-momento-head { display:grid; grid-template-columns:1fr; gap:16px; margin-bottom:36px; align-items:end; }
      @media (min-width:900px) { .hh-momento-head { grid-template-columns:2fr 1fr; } }
      .hh-momento-cta { display:flex; align-items:center; justify-content:space-between; gap:16px; }
      .hh-rail-mask { overflow:hidden; -webkit-mask-image:linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent); mask-image:linear-gradient(90deg, transparent, #000 5%, #000 95%, transparent); padding:20px 0 60px; will-change:margin-left; }
      .hh-rail { display:flex; gap:24px; width:max-content; animation:hit-rail 42s linear infinite; }
      .hh-rail:hover { animation-play-state:paused; }
      .hh-rail-empty { animation:none; opacity:.5; }
      .hh-rail-card { display:block; width:clamp(200px, 22vw, 284px); flex:none; background:var(--card); border:1px solid var(--line); border-radius:18px; padding:14px; transition:border-color .25s, box-shadow .25s, transform .25s; color:inherit; }
      .hh-rail-card:hover { border-color:var(--coral); box-shadow:0 20px 40px rgba(255,68,56,.15); }
      .hh-rail-img { position:relative; aspect-ratio:63/88; background:repeating-linear-gradient(135deg, #1a1a1a 0 8px, #111 8px 16px); border-radius:13px; overflow:hidden; margin-bottom:12px; }
      .hh-rail-img img { width:100%; height:100%; object-fit:cover; }
      .hh-lang-badge { position:absolute; top:8px; right:8px; background:var(--coral); color:#080808; font-family:var(--mono); font-weight:700; font-size:10px; padding:4px 8px; border-radius:4px; letter-spacing:.1em; }
      .hh-rail-skel { height:400px; padding:0; background:none; border:0; }
      .hh-rail-name { font-weight:700; font-size:15px; line-height:1.2; margin-bottom:6px; }
      .hh-rail-meta { color:rgba(255,255,255,.5); font-size:11px; margin-bottom:10px; }
      .hh-rail-price { color:var(--coral); font-weight:800; font-size:22px; letter-spacing:-.02em; }
      .hh-momento-foot { display:flex; align-items:center; gap:8px; justify-content:flex-end; }
      .hh-tick { width:8px; height:8px; border-radius:50%; background:var(--coral); animation:hit-tick 1.6s ease-in-out infinite; }

      /* 03 — POR QUÉ */
      .hh-porque { padding:clamp(60px, 8vw, 120px) 0; }
      .hh-porque-grid { display:grid; grid-template-columns:1fr; gap:40px; }
      @media (min-width:900px) { .hh-porque-grid { grid-template-columns:1fr 2fr; gap:80px; } }
      .hh-porque-list { list-style:none; padding:0; margin:0; }
      .hh-porque-list li { display:grid; grid-template-columns:56px 1fr; gap:6px 16px; padding:28px 0; border-top:1px solid var(--line); transition:border-color .25s; }
      @media (min-width:700px) { .hh-porque-list li { grid-template-columns:64px 1fr 1fr; gap:24px; } }
      .hh-porque-list li:hover { border-top-color:var(--coral); }
      .hh-porque-list li span { color:var(--coral); font-family:var(--mono); font-weight:700; font-size:13px; align-self:start; }
      .hh-porque-list li h3 { font-weight:700; font-size:clamp(20px, 2.4vw, 26px); letter-spacing:-.02em; margin:0 0 6px; grid-column:2 / -1; }
      .hh-porque-list li p { color:rgba(255,255,255,.6); font-size:14px; line-height:1.55; margin:0; grid-column:2 / -1; }
      @media (min-width:700px) { .hh-porque-list li h3 { grid-column:2 / 3; margin:0; } .hh-porque-list li p { grid-column:3 / 4; } }

      /* 04 — TIENDAS */
      .hh-tiendas { position:relative; padding:clamp(60px, 8vw, 120px) 0; background:var(--bg2); overflow:hidden; }
      .hh-ghost { position:absolute; right:-2%; bottom:-40px; font-weight:900; font-size:clamp(90px, 15vw, 200px); color:transparent; -webkit-text-stroke:1px rgba(255,255,255,.06); pointer-events:none; will-change:transform; }
      .hh-tiendas-grid { position:relative; display:grid; grid-template-columns:1fr; gap:40px; }
      @media (min-width:900px) { .hh-tiendas-grid { grid-template-columns:5fr 7fr; gap:60px; align-items:start; } }
      .hh-btn-white { display:inline-block; background:#fff; color:#080808; font-weight:800; padding:14px 26px; border-radius:999px; margin-top:20px; transition:background .2s, color .2s; }
      .hh-btn-white:hover { background:var(--coral); color:#fff; }
      .hh-tiendas-logos { display:grid; grid-template-columns:repeat(2, 1fr); border:1px solid var(--line2); border-radius:20px; overflow:hidden; background:#0A0A0A; }
      @media (min-width:700px) { .hh-tiendas-logos { grid-template-columns:repeat(3, 1fr); } }
      .hh-tienda-cell { min-height:150px; display:flex; align-items:center; justify-content:center; border-right:1px solid var(--line); border-bottom:1px solid var(--line); color:rgba(255,255,255,.55); transition:background .25s; padding:24px; }
      .hh-tienda-cell:hover { background:rgba(255,68,56,.07); }
      .hh-tienda-cell img { max-width:100%; max-height:88px; width:auto; height:auto; object-fit:contain; opacity:.85; transition:opacity .25s; }
      .hh-tienda-cell:hover img { opacity:1; }
      .hh-tienda-placeholder { color:rgba(255,255,255,.25); }
      .hh-tienda-placeholder:hover { background:transparent; }

      /* 06 — FAQ */
      .hh-faq { position:relative; padding:clamp(60px, 8vw, 120px) 0; overflow:hidden; }
      .hh-faq-grid { position:relative; display:grid; grid-template-columns:1fr; gap:40px; }
      @media (min-width:900px) { .hh-faq-grid { grid-template-columns:4fr 8fr; gap:60px; align-items:start; } }
      .hh-faq-list { list-style:none; padding:0; margin:0; }
      .hh-faq-list li { display:grid; grid-template-columns:44px 1fr; gap:16px; padding:26px 0; border-top:1px solid var(--line); }
      @media (min-width:700px) { .hh-faq-list li { grid-template-columns:56px 1fr 1.15fr; gap:20px; } }
      .hh-faq-list li span { color:var(--coral); font-family:var(--mono); font-weight:700; font-size:13px; }
      .hh-faq-list li h3 { font-weight:700; font-size:clamp(17px, 1.8vw, 22px); letter-spacing:-.02em; margin:0 0 4px; }
      @media (min-width:700px) { .hh-faq-list li h3 { grid-column:auto; } }
      .hh-faq-list li p { color:rgba(255,255,255,.6); font-size:14px; line-height:1.55; margin:0; }

      /* 07 — COMUNIDAD */
      .hh-comunidad { position:relative; background:var(--coral); color:#fff; padding:clamp(70px, 10vw, 140px) 0 clamp(90px, 12vw, 160px); overflow:hidden; }
      .hh-comunidad-tex { position:absolute; inset:0; background:repeating-linear-gradient(0deg, rgba(0,0,0,.08) 0 2px, transparent 2px 8px); pointer-events:none; }
      .hh-comunidad-inner { position:relative; }
      .hh-comunidad-head { display:grid; grid-template-columns:1fr; gap:20px; margin-bottom:44px; align-items:end; }
      @media (min-width:900px) { .hh-comunidad-head { grid-template-columns:2fr 1fr; } }
      .hh-comunidad-cards { display:grid; grid-template-columns:1fr; gap:20px; }
      @media (min-width:700px) { .hh-comunidad-cards { grid-template-columns:repeat(3, 1fr); align-items:start; } }
      .hh-social-card { display:flex; flex-direction:column; gap:12px; background:#000; color:#fff; border-radius:20px; padding:26px; min-height:auto; transition:transform .25s, box-shadow .25s; will-change:transform; border:1px solid rgba(255,255,255,.14); position:relative; z-index:2; }
      @media (min-width:700px) { .hh-social-card { min-height:230px; } .hh-social-card-raised { transform:translateY(-22px); } }
      .hh-social-card:hover { transform:translateY(-6px); box-shadow:0 24px 50px rgba(0,0,0,.5); }
      @media (min-width:700px) { .hh-social-card-raised:hover { transform:translateY(-28px); } }
      .hh-social-card-disabled { pointer-events:none; }
      .hh-social-card-disabled .hh-social-cta { color:rgba(255,255,255,.35); }
      .hh-social-name { font-weight:800; font-size:26px; padding-bottom:8px; border-bottom:2px solid; letter-spacing:-.02em; }
      .hh-social-card p { color:rgba(255,255,255,.7); font-size:14px; line-height:1.5; margin:0; flex:1; }
      .hh-social-cta { font-family:var(--mono); font-weight:700; font-size:12px; letter-spacing:.14em; color:var(--coral); }
      .hh-social-soon { color:rgba(255,255,255,.4); }

      /* 08 — VENDEDORES */
      .hh-vender { position:relative; padding:clamp(60px, 8vw, 120px) 0; overflow:hidden; }
      .hh-vender-slab { position:absolute; inset:0; background:linear-gradient(255deg, rgba(255,68,56,.14), transparent 55%); transform:rotate(4deg); pointer-events:none; }
      .hh-vender-grid { position:relative; display:grid; grid-template-columns:1fr; gap:40px; align-items:center; }
      @media (min-width:900px) { .hh-vender-grid { grid-template-columns:7fr 5fr; gap:60px; } }
      .hh-vender-ctas { display:flex; flex-wrap:wrap; gap:12px; margin-top:24px; }
      .hh-btn-coral { display:inline-block; background:var(--coral); color:#fff; font-weight:800; padding:16px 30px; border-radius:999px; transition:background .2s; }
      .hh-btn-coral:hover { background:var(--coral-h); }
      .hh-btn-ghost { display:inline-block; background:transparent; color:#fff; font-weight:700; padding:16px 30px; border-radius:999px; border:1px solid var(--line2); transition:border-color .2s; }
      .hh-btn-ghost:hover { border-color:#fff; }
      .hh-vender-fan { position:relative; height:400px; display:none; will-change:transform; }
      @media (min-width:900px) { .hh-vender-fan { display:block; } }
      .hh-vender-card { position:absolute; top:20px; border-radius:14px; background:repeating-linear-gradient(135deg, #1a1a1a 0 12px, #111 12px 24px); border:1px solid rgba(255,255,255,.08); box-shadow:0 30px 60px rgba(0,0,0,.6); overflow:hidden; }
      .hh-vender-card img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; }
      .hh-vender-a { width:230px; height:322px; left:10%; transform:rotate(-8deg); }
      .hh-vender-b { width:260px; height:364px; right:10%; transform:rotate(7deg); border-color:var(--coral); box-shadow:0 40px 80px rgba(255,68,56,.18); }

      /* KEYFRAMES */
      @keyframes hit-marquee { from { transform:translateX(0); } to { transform:translateX(-50%); } }
      @keyframes hit-rail { from { transform:translateX(0); } to { transform:translateX(-50%); } }
      @keyframes hit-float-a { 0%,100% { transform:rotate(-14deg) translateY(0); } 50% { transform:rotate(-14deg) translateY(-22px); } }
      @keyframes hit-float-b { 0%,100% { transform:rotate(-3deg) translateY(0); } 50% { transform:rotate(-3deg) translateY(18px); } }
      @keyframes hit-float-c { 0%,100% { transform:rotate(9deg) translateY(0); } 50% { transform:rotate(9deg) translateY(-16px); } }
      @keyframes hit-pulse { 0%,100% { opacity:1; box-shadow:0 0 0 0 rgba(52,211,153,.5); } 50% { opacity:.75; box-shadow:0 0 0 8px rgba(52,211,153,0); } }
      @keyframes hit-drift { 0%,100% { transform:translate3d(0,0,0) scale(1); } 50% { transform:translate3d(-40px,30px,0) scale(1.1); } }
      @keyframes hit-tick { 0%,100% { opacity:.35; } 50% { opacity:1; } }

      @media (prefers-reduced-motion: reduce) {
        .hh-ticker-track, .hh-rail, .hh-card-a, .hh-card-b, .hh-card-c, .hh-halo, .hh-pulse, .hh-tick { animation:none !important; }
      }
    `}</style>
  );
}
